import dayjs from '../../../utils/dayjs';
import { BlockStack, Button, DataTable, InlineStack, Link, Text } from '@shopify/polaris';
import { DeleteIcon, RotateLeftIcon } from '@shopify/polaris-icons';
import { useDeleteConfirm } from 'context/DeleteConfirmContext';
import { useNotification } from 'context/NotificationContext';

import __helpers from 'helpers/index';
import { useMemo, useCallback, useState } from 'react';
import { useGetDeletedTickets, useMutateTicketpermanentlyDelete, useMutateTicketUndelete } from 'queries/ticket.query';
import Capacity from 'components/Capacity';
import { TypedTripManifest } from 'queries/trip_manifest.query';

export default function DeletedTicketsList({ manifest }: { manifest: TypedTripManifest }) {
  const { addNotification } = useNotification();
  const { showDeleteConfirm } = useDeleteConfirm();

  const rootSearchPath = window.location.search;
  const StringQuery: Record<string, any> = useMemo(() => {
    return __helpers.ExtractUrl(rootSearchPath);
  }, [rootSearchPath]);

  const deletedTicketQueryParams = useMemo(() => {
    if (!StringQuery?.journey_id) return;
    return {
      journey_id: StringQuery.journey_id,
      trip_manifest_id: manifest?.id || '',
      limit: 400,
    };
  }, [StringQuery?.journey_id, manifest?.id]);

  const {
    data: deletedTicketData,
    isLoading: isDeletedTicketLoading,
    refetch: refetchDeletedTicketData,
  } = useGetDeletedTickets(deletedTicketQueryParams);

  const { mutateAsync: undeleteTicket } = useMutateTicketUndelete();
  const { mutateAsync: deleteTicketPermanently, isPending: isDeletingTicketPermanently } = useMutateTicketpermanentlyDelete();

  const [undeleteLoadingTicketId, setUndeleteLoadingTicketId] = useState<string | number | bigint | null>(null);

  const handleUndeleteTicket = useCallback(
    async (ticket_id: string | number | bigint) => {
      try {
        setUndeleteLoadingTicketId(ticket_id);
        await undeleteTicket(ticket_id);
        addNotification('info', 'Đã khôi phục vé.');
        refetchDeletedTicketData();
      } catch (error: any) {
        addNotification('error', error?.message || 'Lỗi khi khôi phục vé.');
      } finally {
        setUndeleteLoadingTicketId(null);
      }
    },
    [addNotification, refetchDeletedTicketData, undeleteTicket]
  );

  const handlePermanentDeleteTicket = useCallback(
    async (ticket_id: any) => {
      const confirmed = await showDeleteConfirm('Xóa vé vĩnh viễn?', 'Vé sẽ bị xóa vĩnh viễn và không thể khôi phục.');
      if (!confirmed) return;
      try {
        await deleteTicketPermanently(ticket_id);
        addNotification('info', 'Đã xóa vé vĩnh viễn.');
        refetchDeletedTicketData();
      } catch (error: any) {
        addNotification('error', error?.message || 'Lỗi khi xóa vé.');
      }
    },
    [addNotification, deleteTicketPermanently, refetchDeletedTicketData, showDeleteConfirm]
  );

  return (
    <BlockStack gap="200">
      {isDeletedTicketLoading && (
        <InlineStack align="start" blockAlign="center" gap="200">
          <Text as="p" tone="subdued">
            Đang tải danh sách vé đã xóa...
          </Text>
        </InlineStack>
      )}
      {!isDeletedTicketLoading && (!deletedTicketData || deletedTicketData?.length === 0) && (
        <Text as="p" tone="subdued">
          Không có vé đã xóa của chuyến này
        </Text>
      )}
      {!isDeletedTicketLoading && !!deletedTicketData?.length && (
        <DataTable
          hideScrollIndicator
          columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'numeric']}
          headings={['ID', 'Mã đơn hàng', 'SL vé', 'Khách hàng', 'Hành trình', 'Ngày đi', 'Ngày đặt', 'Update lần cuối', 'Người đặt', 'Thao tác']}
          rows={
            deletedTicketData.map((item) => {
              const orderCreatedAt = item?.createdAt ? new Date(Number(item.createdAt)) : null;
              const departureDate = item?.orders?.departure_date ? new Date(Number(item?.orders?.departure_date)) : null;
              const updatedAt = item?.updatedAt ? dayjs(new Date(Number(item.updatedAt))).format('HH:mm DD-MM-YYYY') : null;
              const customerId = item?.orders?.customer_data?.user_id || item?.orders?.customer_id;
              const customerName = item?.orders?.customer_data?.display_name || item?.orders?.customer_name || '-';
              const customerPhone = item?.orders?.customer_data?.user_phonenumber || item?.orders?.customer_phone || '-';
              const pickupAddress = item?.orders?.pickup_address || '-';
              const dropoffAddress = item?.orders?.dropoff_address || '-';
              return [
                <Text as="span">{item?.ticket_id || '-'}</Text>,
                <Text as="span">{item?.orders?.order_code || item?.order_id || '-'}</Text>,
                <Text as="span">{item?.orders?.total_seat || '0'}</Text>,
                customerId ? (
                  <Link removeUnderline url={`/customer/view/${customerId}`}>
                    {customerName} - {customerPhone}
                  </Link>
                ) : (
                  <Text as="span">
                    {customerName} - {customerPhone}
                  </Text>
                ),
                <BlockStack gap="050">
                  <Text as="span" variant="bodySm">
                    Đến: {dropoffAddress}
                  </Text>
                  <Text as="span" variant="bodySm">
                    Đi: {pickupAddress}
                  </Text>
                </BlockStack>,
                <Text as="span">{departureDate ? dayjs(departureDate).format('HH:mm DD-MM-YYYY') : '-'}</Text>,
                <Text as="span">{orderCreatedAt ? dayjs(orderCreatedAt).format('HH:mm DD-MM-YYYY') : '-'}</Text>,
                <Text as="span">{updatedAt}</Text>,
                <Text as="span">{item?.orders?.author_data?.display_name || '-'}</Text>,
                <InlineStack gap="200" blockAlign="center" align="end" wrap={false}>
                  <Button
                    size="slim"
                    icon={RotateLeftIcon}
                    variant="plain"
                    onClick={() => {
                      if (!item?.ticket_id) return;
                      handleUndeleteTicket(item.ticket_id);
                    }}
                    loading={undeleteLoadingTicketId === item?.ticket_id}
                  >
                    Khôi phục vào đây
                  </Button>
                  <Capacity current_user_can={'delete_ticket'}>
                    <Button
                      size="slim"
                      variant="plain"
                      tone="critical"
                      icon={DeleteIcon}
                      onClick={() => {
                        if (!item?.ticket_id) return;
                        handlePermanentDeleteTicket(item.ticket_id);
                      }}
                      loading={isDeletingTicketPermanently}
                    >
                      Xóa vĩnh viễn
                    </Button>
                  </Capacity>
                </InlineStack>,
              ];
            }) || []
          }
        />
      )}
    </BlockStack>
  );
}

